import * as XLSX from 'xlsx';
export interface ValidationError {
  sheetName: string;
  columnName: string;
  rowNumber: number;
  expectedType: string;
  actualValue: any;
  allowedValues?: string[];
}
type IdSets = {
  Menu: Set<string>;
  Category: Set<string>;
  Item: Set<string>;
  Modifier: Set<string>;
  ModifierOption: Set<string>;
  ModifierGroup: Set<string>;
  Tag: Set<string>;
  Allergen: Set<string>;
  Setting: Set<string>;
};
type RelationValidationConfig = {
  sheetName: string;
  sheetData: any[][];
  errors: ValidationError[];
  relationColumns: string[]; // e.g., ['categoryId', 'itemId']
  referenceSets: Record<string, Set<string>>; // e.g., { categoryId: Set<string>, itemId: Set<string> }
  relationName?: string; // optional for error label, e.g., "Category + Item"
};
type ExpectedTypes = Record<string, Record<string, string | string[] | number[]>>;
type FormattedValidationError = {
  sheetName: string;
  rowNumber: number;
  columnName: string;
  location: string;
  found: string;
  allowed: string | null;
  detailed: string;
  actualValue: unknown;
  allowedValues?: unknown[];
};

type SettingOwnerSheet = 'Menu' | 'Category' | 'Item';
type SettingUsage = {
  sheetName: SettingOwnerSheet;
  rowNumber: number;
  settingId: string;
};
export const MenuColors = [
  '#FCA98F',
  '#F7B897',
  '#FED4A0',
  '#FDD685',
  '#FEF2AD',
  '#E2EEAF',
  '#D7EB9E',
  '#C2F1CA',
  '#9FE7FE',
  '#85C6E9',
];

export const CategoryColors = [
  '#FF7B42',
  '#FFAD73',
  '#FDD092',
  '#FECA6F',
  '#FDEB97',
  '#DCEA9F',
  '#C3E68D',
  '#AEE9C1',
  '#7BDCFD',
  '#6FB9E7',
  '#8897D6',
  '#AA8EC6',
  '#C293C0',
  '#C8C5E2',
  '#F59EBE',
  '#F6ACAD',
  '#F9C5C6',
  '#FE9194',
  '#FEA7A9',
  '#FB8E81',
  '#FCA59A',
  '#F0B5AF',
  '#8DC4BA',
  '#ACE3D9',
  '#AFD6CF',
  '#75D1D9',
  '#A9E2E8',
  '#DDD17E',
  '#E4DA98',
  '#EEE4A1',
];
// Constants
const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;
const NUMERIC_REGEX = /^-?\d+(\.\d+)?$/;
const INTEGER_REGEX = /^\d+$/;
const idSets: IdSets = {
  Menu: new Set(),
  Category: new Set(),
  Item: new Set(),
  Modifier: new Set(),
  ModifierOption: new Set(),
  ModifierGroup: new Set(),
  Tag: new Set(),
  Allergen: new Set(),

  Setting: new Set(),
};
const capitalizeFirstLetter = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);
export const validatorType = async (rolesOrStationIds: number[]) => {
  const expectedTypesMap = {
    Employee: {
      employee: {
        'First Name': '30Characters',
        'Last Name': '30Characters',
        Email: 'email',
        'Phone Number': 'phoneNumber',
        Role: rolesOrStationIds,
        'Pay Type': ['Hourly', 'Salary'],
        Salary: 'number',
      },
    },
    'Location Config File': {
      restaurant: {
        bannerImage: 'imageUrl',
        accountId: 'string',
      },
      online_ordering_settings: {
        enableCustomerReviews: 'boolean',
        autoApproveCustomerReviews: 'boolean',
        isDeliveryEnabled: 'boolean',
        isPickupEnabled: 'boolean',
        enableOnlineOrdering: 'boolean',
      },
      restaurant_timings: {
        status: ['open', 'closed'],
        day: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
        startTime: 'time',
        endTime: 'time',
        type: ['restaurant', 'delivery', 'delivery-slot', 'pickup-slot'],
      },
      general_settings: {
        paymentSettings: 'object',
        printerReceiptSettings: 'object',
        tipSettings: 'object',
        digitalReceiptSettings: 'object',
        zReportSettings: 'object',
      },
    },

    Menu: {
      Menu: {
        id: 'number',
        menuName: '30Characters',
        posDisplayName: '60Characters',
        posButtonColor: 'colorCode',
        picture: 'imageUrl',
        sortOrder: 'nonNegativeNumber',
        settingId: 'multipleIds',
      },
      Category: {
        id: 'number',
        categoryName: 'multiName',
        posDisplayName: '60Characters',
        kdsDisplayName: '40Characters',
        color: 'colorCode',
        image: 'imageUrl',
        kioskImage: 'imageUrl',
        parentCategoryId: 'id',
        tagIds: 'multipleIds',
        menuIds: 'multipleIds',
        sortOrder: 'nonNegativeNumber',
        settingId: 'multipleIds',
      },

      'Category ModifierGroups': {
        categoryId: 'number',
        modifierGroupId: 'number',
        sortOrder: 'nonNegativeNumber',
      },

      'Category Modifiers': {
        categoryId: 'number',
        modifierId: 'number',
        sortOrder: 'nonNegativeNumber',
      },

      'Category Items': {
        id: 'number',
        categoryId: 'number',
        itemId: 'number',
        sortOrder: 'nonNegativeNumber',
      },

      Item: {
        id: 'number',
        itemName: 'multiName',
        posDisplayName: '60Characters',
        kdsName: '40Characters',
        itemDescription: 'choiceString',
        itemPicture: 'imageUrl',
        onlineImage: 'imageUrl',
        landscapeImage: 'imageUrl',
        thirdPartyImage: 'imageUrl',
        kioskItemImage: 'imageUrl',
        itemPrice: 'nonNegativeNumber',
        taxLinkedWithParentSetting: 'boolean',
        calculatePricesWithTaxIncluded: 'boolean',
        takeoutException: 'boolean',
        stockStatus: 'stockStatus',
        stockValue: 'optionalInteger',
        orderQuantityLimit: 'boolean',
        minLimit: 'choiceNumber',
        maxLimit: 'choiceNumber',
        noMaxLimit: 'choiceBoolean',
        stationIds: rolesOrStationIds,
        preparationTime: 'choiceNumber',
        calories: 'optionalInteger',
        tagIds: 'multipleIds',
        inheritTagsFromCategory: 'boolean',
        saleCategory: [
          'Food Sales',
          'Beer Sales',
          'Wine Sales',
          'Liquor Sales',
          'Non-Alcoholic Beverages Sales',
          'Non-Food Items',
          'Cocktails',
          'Mocktails',
        ],
        allergenIds: 'multipleIds',
        inheritModifiersFromCategory: 'boolean',
        addonIds: 'multipleIds',
        isSpecialRequest: 'boolean',
        settingId: 'multipleIds',
      },

      'Item Modifiers': {
        itemId: 'number',
        modifierId: 'number',
        sortOrder: 'nonNegativeNumber',
      },

      'Item Modifier Group': {
        itemId: 'number',
        modifierGroupId: 'number',
        sortOrder: 'nonNegativeNumber',
      },

      'Modifier Group': {
        id: 'number',
        groupName: 'string',
        posDisplayName: '60Characters',
        onPrem: 'boolean',
        offPrem: 'boolean',
        modifierIds: 'multipleIds',
      },

      Modifier: {
        id: 'number',
        modifierName: 'multiName',
        posDisplayName: '60Characters',
        isNested: 'boolean',
        addNested: 'boolean',
        modifierOptionPriceType: ['Individual', 'Group', 'NoCharge'],
        isOptional: 'boolean',
        canGuestSelectMoreModifiers: 'boolean',
        multiSelect: 'boolean',
        limitIndividualModifierSelection: 'boolean',
        minSelector: 'number',
        maxSelector: 'number',
        noMaxSelection: 'boolean',
        prefix: 'choiceString',
        pizzaSelection: 'boolean',
        stockStatus: 'boolean',
        price: 'nonNegativeChoiceNumber',
        onPrem: 'boolean',
        offPrem: 'boolean',
        parentModifierId: 'id',
        isSizeModifier: 'boolean',
        modType: ['Required', 'Optional', 'Push Optional'],
      },

      'Modifier Option': {
        id: 'number',
        optionName: '72Characters',
        posDisplayName: '60Characters',
        price: 'nonNegativeNumber',
        isStockAvailable: 'boolean',
        isSizeModifier: 'boolean',
      },

      'Modifier ModifierOptions': {
        modifierId: 'number',
        modifierOptionId: 'number',
        isDefaultSelected: 'boolean',
        // maxLimit: 'number',
        // optionDisplayName: 'string',
        sortOrder: 'nonNegativeNumber',
      },

      Allergen: {
        id: 'number',
        allergenName: 'string',
        iconId: 'number',
        isDefault: 'boolean',
      },

      Tag: {
        id: 'number',
        tagName: 'string',
        iconId: 'number',
        isDefault: 'boolean',
      },
    },
  };
  return expectedTypesMap;
};
export const extractAllIdSetsFromSheets = (workbook: XLSX.WorkBook, idSets: IdSets): IdSets => {
  const sheetToKeyMap: Record<string, keyof IdSets> = {
    Menu: 'Menu',
    Category: 'Category',
    Item: 'Item',
    Modifier: 'Modifier',
    'Modifier Option': 'ModifierOption',
    'Modifier Group': 'ModifierGroup',
    Tag: 'Tag',
    Allergen: 'Allergen',
    Setting: 'Setting',
  };

  const sheetNames = workbook.SheetNames || [];

  Object.entries(sheetToKeyMap).forEach(([expectedSheetName, idSetKey]) => {
    const actualSheetName = sheetNames.find(
      (name) => name.trim().toLowerCase() === expectedSheetName.trim().toLowerCase()
    );
    const sheet = actualSheetName ? workbook.Sheets[actualSheetName] : undefined;
    if (!sheet) return;

    const data = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
    const headers = data[0];
    const rows = data.slice(1);

    const idIndex = headers.indexOf('id');
    if (idIndex === -1) return;

    rows.forEach((row) => {
      const id = row[idIndex];
      if (id !== undefined && id !== null && String(id).trim() !== '') {
        idSets[idSetKey].add(String(id).trim());
      }
    });
  });

  return idSets;
};

/**
 * Extracts modifier IDs from the Modifier sheet where isNested is true.
 * Nested modifiers must not be linked directly to Category or Item.
 */
export const extractNestedModifierIds = (workbook: XLSX.WorkBook): Set<string> => {
  const nestedIds = new Set<string>();
  const sheetNames = workbook.SheetNames || [];
  const actualSheetName = sheetNames.find((name) => name.trim().toLowerCase() === 'modifier');
  if (!actualSheetName) return nestedIds;

  const sheet = workbook.Sheets[actualSheetName];
  if (!sheet) return nestedIds;

  const data = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
  const headers = data[0];
  const rows = data.slice(1);
  const idIndex = headers.indexOf('id');
  const isNestedIndex = headers.indexOf('isNested');
  if (idIndex === -1 || isNestedIndex === -1) return nestedIds;

  const toBoolean = (value: any): boolean => {
    if (value === undefined || value === null) return false;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.trim().toLowerCase() === 'true';
    return false;
  };

  rows.forEach((row) => {
    const id = row[idIndex];
    const isNested = toBoolean(row[isNestedIndex]);
    if (isNested && id !== undefined && id !== null && String(id).trim() !== '') {
      nestedIds.add(String(id).trim());
    }
  });

  return nestedIds;
};

/**
 * Validates that no nested modifier is linked in Category Modifiers or Item Modifiers sheets.
 */
const validateNoNestedModifiersLinked = (
  sheetName: string,
  sheetData: any[][],
  modifierIdColumn: string,
  nestedModifierIds: Set<string>,
  errors: ValidationError[]
): void => {
  if (nestedModifierIds.size === 0) return;

  const headers = sheetData[0];
  const rows = sheetData.slice(1);
  const modifierIdIndex = headers.indexOf(modifierIdColumn);
  if (modifierIdIndex === -1) return;

  rows.forEach((row, rowIndex) => {
    const modifierId = row[modifierIdIndex];
    if (modifierId === undefined || modifierId === null) return;

    const stringId = String(modifierId).trim();
    if (stringId && nestedModifierIds.has(stringId)) {
      errors.push(
        createValidationError(
          sheetName,
          modifierIdColumn,
          rowIndex + 2,
          'Nested modifier should not be directly linked to Category/Item',
          modifierId
        )
      );
    }
  });
};

// Helper function to create validation errors
const createValidationError = (
  sheetName: string,
  columnName: string,
  rowNumber: number,
  expectedType: string,
  actualValue: any,
  allowedValues?: string[]
): ValidationError => ({
  sheetName,
  columnName,
  rowNumber,
  expectedType,
  actualValue,
  ...(allowedValues && { allowedValues }),
});

/**
 * Validate an attribute value against a given expected type or enum
 *
 * @param expectedTypeOrEnum - The expected type or enum of the attribute value
 * @param actualValue - The actual value of the attribute
 * @param sheetName - The name of the sheet containing the attribute
 * @param header - The column header of the attribute
 * @param rowIndex - The row number of the attribute
 * @param addValidationError - A callback to add a validation error to the list
 */
function validateAttributes(
  expectedTypeOrEnum: any,
  actualValue: any,
  sheetName: string,
  header: string,
  rowIndex: number,
  addValidationError: (
    sheetName: string,
    columnName: string,
    rowNumber: number,
    expectedType: string,
    actualValue: any,
    allowedValues?: string[]
  ) => void
) {
  const expectedType = expectedTypeOrEnum?.type;
  const allowedValues = expectedTypeOrEnum?.enum;
  const pattern = expectedTypeOrEnum?.pattern;
  const required = expectedTypeOrEnum?.required;
  const caseSensitive = expectedTypeOrEnum?.caseSensitive;

  if (required && (actualValue === null || actualValue === undefined)) {
    addValidationError(sheetName, header, rowIndex + 2, 'non-empty', 'Missing required value');
    return;
  }

  // Skip validation if value is optional and is not provided
  if (actualValue === null || actualValue === undefined) {
    return;
  }

  if (expectedType === 'string' && typeof actualValue !== 'string') {
    addValidationError(sheetName, header, rowIndex + 2, 'string', actualValue);
    return;
  }
  if (expectedType === 'number' && typeof actualValue !== 'number') {
    addValidationError(sheetName, header, rowIndex + 2, 'number', actualValue);
    return;
  }
  if (expectedType === 'boolean' && typeof actualValue !== 'boolean') {
    addValidationError(sheetName, header, rowIndex + 2, 'boolean', actualValue);
    return;
  }

  if (expectedType === 'string' && pattern && !new RegExp(pattern).test(actualValue)) {
    addValidationError(sheetName, header, rowIndex + 2, `string (${pattern})`, actualValue);
    return;
  }

  if (allowedValues) {
    const isMatch = caseSensitive
      ? allowedValues.includes(actualValue)
      : allowedValues.some((val: any) => val.toLowerCase() === actualValue.toLowerCase());

    if (!isMatch) {
      addValidationError(sheetName, header, rowIndex + 2, `enum (${allowedValues.join(', ')})`, actualValue);
      return;
    }
  }
}

// Validate sheet existence
const validateSheetExistence = (workbook: any, expectedTypes: ExpectedTypes, errors: ValidationError[]): void => {
  Object.keys(expectedTypes).forEach((expectedSheetName) => {
    if (!workbook.SheetNames.includes(expectedSheetName)) {
      errors.push(createValidationError(expectedSheetName, 'N/A', 0, 'Sheet missing', 'Sheet not found'));
    }
  });
};

// Validate missing columns
const validateMissingColumns = (
  sheetName: string,
  headers: string[],
  expectedColumns: string[],
  errors: ValidationError[]
): void => {
  expectedColumns.forEach((expectedColumn) => {
    if (!headers.includes(expectedColumn)) {
      errors.push(createValidationError(sheetName, expectedColumn, 0, 'Column missing', 'column not found'));
    }
  });
};

// Validate duplicate IDs
const validateDuplicateIds = (sheetName: string, headers: string[], rows: any[][], errors: ValidationError[]): void => {
  const idColumnIndex = headers.indexOf('id');
  if (idColumnIndex === -1) return;

  const idMap: { [key: string]: number } = {};

  rows.forEach((row, rowIndex) => {
    if (row.every((cell) => cell === undefined)) return;

    const idValue = row[idColumnIndex];
    if (idMap[idValue]) {
      errors.push(createValidationError(sheetName, 'id', rowIndex + 2, 'Unique id', idValue));
    } else {
      idMap[idValue] = rowIndex + 2;
    }
  });
};
// Validate sortOrder
//
const validateSortOrderForSheets = (
  sheetName: string,
  headers: string[],
  rows: any[][],
  errors: ValidationError[],
  allowedSheets: string[]
): void => {
  // run only if sheet is allowed
  if (!allowedSheets.includes(sheetName)) return;

  const sortOrderIndex = headers.indexOf('sortOrder');
  if (sortOrderIndex === -1) return;

  // Define junction table configurations: sheetName -> parentColumnName
  const junctionTableConfigs: Record<string, string> = {
    'Category Items': 'categoryId',
    'Category Modifiers': 'categoryId',
    'Category ModifierGroups': 'categoryId',
    'Item Modifiers': 'itemId',
    'Item Modifier Group': 'itemId',
    'Modifier ModifierOptions': 'modifierId',
  };

  const parentColumn = junctionTableConfigs[sheetName];

  if (parentColumn) {
    // Junction table: validate uniqueness per parent entity
    validateJunctionTableSortOrder(sheetName, headers, rows, errors, sortOrderIndex, parentColumn);
  } else {
    // Direct sheet: validate global uniqueness
    validateDirectSheetSortOrder(sheetName, headers, rows, errors, sortOrderIndex);
  }
};

/**
 * Validates sortOrder for direct sheets (Menu, Category, Modifier, Modifier Option).
 * SortOrder must be unique globally within the sheet and not exceed the total number of records.
 */
const validateDirectSheetSortOrder = (
  sheetName: string,
  headers: string[],
  rows: any[][],
  errors: ValidationError[],
  sortOrderIndex: number
): void => {
  const sortOrderMap: Record<string | number, number> = {};

  // Count total non-empty rows
  // const totalRecords = rows.filter((row) => !row.every((cell) => cell === undefined)).length;

  rows.forEach((row, rowIndex) => {
    if (row.every((cell) => cell === undefined)) return;

    const value = row[sortOrderIndex];

    // ignore empty sortOrder
    if (value === undefined || value === null || value === '') return;

    // const numValue = typeof value === 'number' ? value : parseFloat(String(value));

    // // Check if sortOrder exceeds total records
    // if (!isNaN(numValue) && numValue > totalRecords) {
    //   errors.push(
    //     createValidationError(
    //       sheetName,
    //       'sortOrder',
    //       rowIndex + 2,
    //       `sortOrder must not exceed ${totalRecords} (total number of records in sheet)`,
    //       value,
    //       [`Maximum allowed sortOrder is ${totalRecords}`]
    //     )
    //   );
    // }

    if (sortOrderMap[value]) {
      errors.push(
        createValidationError(sheetName, 'sortOrder', rowIndex + 2, 'sortOrder must be unique', value, [
          `Duplicate sortOrder found at row ${sortOrderMap[value]}`,
        ])
      );
    } else {
      sortOrderMap[value] = rowIndex + 2;
    }
  });
};

/**
 * Validates sortOrder for junction tables (Category Items, Item Modifiers, etc.).
 * SortOrder must be unique per parent entity (e.g., per categoryId, per itemId).
 */
const validateJunctionTableSortOrder = (
  sheetName: string,
  headers: string[],
  rows: any[][],
  errors: ValidationError[],
  sortOrderIndex: number,
  parentColumn: string
): void => {
  const parentColumnIndex = headers.indexOf(parentColumn);
  if (parentColumnIndex === -1) return;

  // Group rows by parent ID
  const rowsByParent = new Map<string | number, Array<{ row: any[]; rowIndex: number; sortOrder: any }>>();

  rows.forEach((row, rowIndex) => {
    if (row.every((cell) => cell === undefined)) return;

    const parentId = row[parentColumnIndex];
    const sortOrderValue = row[sortOrderIndex];

    // Skip if parent ID is missing
    if (parentId === undefined || parentId === null || parentId === '') return;

    // Skip if sortOrder is empty (will be handled by required validation if needed)
    if (sortOrderValue === undefined || sortOrderValue === null || sortOrderValue === '') return;

    const parentKey = String(parentId).trim();
    if (!rowsByParent.has(parentKey)) {
      rowsByParent.set(parentKey, []);
    }
    rowsByParent.get(parentKey)!.push({ row, rowIndex, sortOrder: sortOrderValue });
  });

  // Validate uniqueness and max value within each parent group
  rowsByParent.forEach((rowsForParent, parentId) => {
    const sortOrderMap: Record<string | number, number> = {};
    // const maxAllowedSortOrder = rowsForParent.length;

    rowsForParent.forEach(({ row, rowIndex, sortOrder }) => {
      const sortOrderKey = String(sortOrder).trim();
      // const numValue = typeof sortOrder === 'number' ? sortOrder : parseFloat(sortOrderKey);

      // // Check if sortOrder exceeds the number of records for this parent
      // if (!isNaN(numValue) && numValue > maxAllowedSortOrder) {
      //   errors.push(
      //     createValidationError(
      //       sheetName,
      //       'sortOrder',
      //       rowIndex + 2,
      //       `sortOrder must not exceed ${maxAllowedSortOrder} for ${parentColumn} ${parentId} (total records: ${maxAllowedSortOrder})`,
      //       sortOrder,
      //       [`Maximum allowed sortOrder is ${maxAllowedSortOrder} for this ${parentColumn}`]
      //     )
      //   );
      // }

      if (sortOrderMap[sortOrderKey] !== undefined) {
        const duplicateRowNum = sortOrderMap[sortOrderKey];
        errors.push(
          createValidationError(
            sheetName,
            'sortOrder',
            rowIndex + 2,
            `sortOrder must be unique within ${parentColumn} ${parentId}`,
            sortOrder,
            [`Duplicate sortOrder found at row ${duplicateRowNum} for the same ${parentColumn}`]
          )
        );
      } else {
        sortOrderMap[sortOrderKey] = rowIndex + 2;
      }
    });
  });
};

// Validate empty cells
const validateEmptyCells = (
  sheetName: string,
  headers: string[],
  row: any[],
  rowIndex: number,
  errors: ValidationError[]
): boolean => {
  if (row.some((cell) => String(cell).trim() === '')) {
    const emptyColumns = headers.filter((header, colIndex) => String(row[colIndex]).trim() === '');
    emptyColumns.forEach((emptyColumn) => {
      errors.push(createValidationError(sheetName, emptyColumn, rowIndex + 2, 'Non-empty text', 'Empty cell'));
    });
    return true; // Has empty cells
  }
  return false; // No empty cells
};

// Validate enum values
const validateEnumValues = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  expectedValues: (string | number)[],
  errors: ValidationError[]
): void => {
  const normalizedExpectedValues = expectedValues.map((value) =>
    value === null || value === undefined ? '' : String(value).trim()
  );

  const rawActualValues =
    actualValue === null || actualValue === undefined
      ? []
      : String(actualValue)
          .split(',')
          .map((value) => value);

  const inferredType = typeof expectedValues[0];
  const normalizedActualValues = rawActualValues.map((value) => {
    if (inferredType === 'number') return Number(value);
    if (inferredType === 'boolean') return value.toLowerCase() === 'true';
    if (inferredType === 'string' && HEX_COLOR_REGEX.test(value)) {
      return value.toUpperCase();
    }
    return value;
  });

  const invalidValues = normalizedActualValues.filter((value) => !normalizedExpectedValues.includes(String(value)));

  if (invalidValues.length > 0) {
    errors.push({
      sheetName,
      columnName: header,
      rowNumber: rowIndex + 2,
      expectedType: `enum (${expectedValues.join(', ')})`,
      allowedValues: normalizedExpectedValues,
      actualValue,
    });
  }
};

// Validate string type
const validateString = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  if (typeof actualValue !== 'string' || actualValue.trim() === '') {
    errors.push(createValidationError(sheetName, header, rowIndex + 2, 'string (non-empty text)', actualValue));
  }
};

// Validate character length
const validateCharacterLength = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  maxLength: number,
  errors: ValidationError[],
  minLength: number
): void => {
  const value = typeof actualValue === 'string' ? actualValue.trim() : '';

  const isNameField =
    sheetName.toLowerCase() === 'employee' &&
    (header.toLowerCase() === 'first name' || header.toLowerCase() === 'last name');

  // Validate for numbers in name fields
  if (isNameField && value.length > 0) {
    const hasNumbers = /\d/.test(value);
    if (hasNumbers) {
      errors.push(
        createValidationError(sheetName, header, rowIndex + 2, `Employee ${header} cannot contain numbers`, actualValue)
      );
      return; // Exit early since numbers are not allowed
    }
  }

  if (value.length < minLength || value.length > maxLength) {
    errors.push(
      createValidationError(
        sheetName,
        header,
        rowIndex + 2,
        `Minimum ${minLength} and maximum ${maxLength} characters`,
        actualValue
      )
    );
  } else {
    validateName(sheetName, header, rowIndex, actualValue, errors);
  }
};

// Validate phone number
const validatePhoneNumber = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  const phoneNumber = actualValue.toString().trim();
  if (!/^\+\d{11}$/.test(phoneNumber)) {
    errors.push(createValidationError(sheetName, header, rowIndex + 2, 'phoneNumber (11 digits)', actualValue));
  }
};

// Validate timezone
const validateTimezone = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: actualValue.toString().trim() }).resolvedOptions();
  } catch (e) {
    errors.push(
      createValidationError(sheetName, header, rowIndex + 2, 'timezone (e.g., America/New_York)', actualValue)
    );
  }
};

// Validate image URL
const validateImageUrl = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  const imageRegex = /\.(jpeg|jpg|png|gif|bmp|webp)$/i;
  const imageUrl = actualValue?.toString().trim() || '';

  // Block if empty (optional depending on context)
  if (!imageUrl) return;

  // Block data URIs or JS URIs
  const isMaliciousScheme = /^(javascript|data):/i.test(imageUrl);
  const isMissingProtocol = !/^https?:\/\//i.test(imageUrl);
  const isFileExtensionInvalid = !imageRegex.test(imageUrl);

  if (isMaliciousScheme) {
    errors.push(
      createValidationError(
        sheetName,
        header,
        rowIndex + 2,
        'Invalid URL scheme (javascript:, data: not allowed)',
        actualValue
      )
    );
  } else if (isMissingProtocol) {
    errors.push(
      createValidationError(sheetName, header, rowIndex + 2, 'URL must start with http:// or https://', actualValue)
    );
  } else if (isFileExtensionInvalid) {
    errors.push(
      createValidationError(
        sheetName,
        header,
        rowIndex + 2,
        'imageUrl must end with .jpeg, .jpg, .png, etc.',
        actualValue
      )
    );
  }
};

// Validate time format
const validateTime = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  const cleanedValue = actualValue?.toString().trim().replace(/['"]/g, '') || '';
  if (!cleanedValue) {
    errors.push(createValidationError(sheetName, header, rowIndex + 2, 'time (HH:mm:ss)', 'Missing time value'));
  } else {
    const timeRegex = /^\d{2}:\d{2}:\d{2}$/;
    if (!timeRegex.test(cleanedValue)) {
      errors.push(createValidationError(sheetName, header, rowIndex + 2, 'time (HH:mm:ss)', actualValue));
    }
  }
};

// Validate name format
const validateName = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  const stringValue = actualValue?.toString().trim() || '';

  // Regex for full-string validation - allows all special characters
  const fullRegex = /^.+$/;

  // Regex for validating one character at a time - allows all special characters
  const charRegex = /^.$/;

  if (!stringValue) {
    errors.push(
      createValidationError(
        sheetName,
        header,
        rowIndex + 2,
        'Missing required value. Only letters, numbers, spaces, and approved symbols are allowed.',
        'Missing required value'
      )
    );
  } else if (!fullRegex.test(stringValue)) {
    const invalidChars: string = stringValue
      .split('')
      .filter((char: string, index: number, arr: string[]): boolean => {
        return !charRegex.test(char) && arr.indexOf(char) === index;
      })
      .join(', ');

    errors.push(
      createValidationError(
        sheetName,
        header,
        rowIndex + 2,
        `Invalid character(s) found ( ${invalidChars} ). Only letters, numbers, spaces, and approved symbols are allowed.`,
        actualValue
      )
    );
  }
};

const validateNameField = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  // Convert to string and trim
  const stringValue = actualValue?.toString().trim();

  // If null or undefined → invalid (required field)
  if (stringValue === undefined || stringValue === null || stringValue === '') {
    errors.push(createValidationError(sheetName, header, rowIndex + 2, '2 - 40 characters', actualValue));
    return;
  }

  // Check max length
  if (stringValue.length > 40) {
    errors.push(createValidationError(sheetName, header, rowIndex + 2, '2 - 40 characters', actualValue));
    return;
  }

  // Check if it's numeric (1 character allowed)
  if (/^\d+$/.test(stringValue)) {
    if (stringValue.length < 1) {
      errors.push(createValidationError(sheetName, header, rowIndex + 2, '1 - 40 characters', actualValue));
    }
  } else {
    // Non-numeric: must be at least 2 characters
    if (stringValue.length < 2) {
      errors.push(createValidationError(sheetName, header, rowIndex + 2, '2 - 40 characters', actualValue));
    }
  }
};

// Validate ID format
const validateId = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  const idValue = actualValue?.toString().trim() || '';
  if (idValue && !/^\d+$/.test(idValue)) {
    errors.push(createValidationError(sheetName, header, rowIndex + 2, 'id (only numeric values)', actualValue));
  }
  if (sheetName === 'Category' && header === 'parentCategoryId') {
    validateParentIdFromSheet(sheetName, header, rowIndex, actualValue, idSets.Category, errors);
  }
  if (sheetName === 'Modifier' && header === 'parentModifierId') {
    validateParentIdFromSheet(sheetName, header, rowIndex, actualValue, idSets.Modifier, errors);
  }
};

// Validate number (optional)
const validateChoiceNumber = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  const numberValue = actualValue?.toString().trim() || '';
  if (numberValue && !NUMERIC_REGEX.test(numberValue)) {
    errors.push(
      createValidationError(sheetName, header, rowIndex + 2, 'number (integer or decimal-point)', actualValue)
    );
  }
};

// Validate number (required)
const validateNumber = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  const numberValue = actualValue?.toString().trim();
  if (!numberValue) {
    errors.push(
      createValidationError(
        sheetName,
        header,
        rowIndex + 2,
        'number (non-empty, integer or decimal-point)',
        actualValue
      )
    );
  } else if (!NUMERIC_REGEX.test(numberValue)) {
    errors.push(
      createValidationError(
        sheetName,
        header,
        rowIndex + 2,
        'number (non-empty, integer or decimal-point)',
        actualValue
      )
    );
  }
};

// Validate non-negative number (required)
const validateNonNegativeNumber = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  const numberValue = actualValue?.toString().trim();
  if (!numberValue) {
    errors.push(
      createValidationError(
        sheetName,
        header,
        rowIndex + 2,
        'number (non-empty, integer or decimal-point)',
        actualValue
      )
    );
  } else if (!NUMERIC_REGEX.test(numberValue)) {
    errors.push(
      createValidationError(
        sheetName,
        header,
        rowIndex + 2,
        'number (non-empty, integer or decimal-point)',
        actualValue
      )
    );
  } else {
    const numValue = parseFloat(numberValue);
    if (numValue < 0) {
      errors.push(
        createValidationError(sheetName, header, rowIndex + 2, 'Negative number is not allowed.', actualValue)
      );
    }
  }
};

// Validate non-negative number (optional)
const validateNonNegativeChoiceNumber = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  const numberValue = actualValue?.toString().trim() || '';
  if (numberValue) {
    if (!NUMERIC_REGEX.test(numberValue)) {
      errors.push(
        createValidationError(sheetName, header, rowIndex + 2, 'number (integer or decimal-point)', actualValue)
      );
    } else {
      const numValue = parseFloat(numberValue);
      if (numValue < 0) {
        errors.push(
          createValidationError(sheetName, header, rowIndex + 2, 'Negative pricing is not allowed.', actualValue)
        );
      }
    }
  }
};

// Validate boolean
const validateBoolean = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[],
  isOptional: boolean
): void => {
  const booleanValue = actualValue?.toString().trim();

  if (!booleanValue) {
    if (!isOptional) {
      errors.push(createValidationError(sheetName, header, rowIndex + 2, 'boolean (true or false)', 'Missing value'));
    }
  } else {
    const normalizedValue = booleanValue.toLowerCase();
    const isValidBoolean = normalizedValue === 'true' || normalizedValue === 'false';

    if (!isValidBoolean) {
      errors.push(createValidationError(sheetName, header, rowIndex + 2, 'boolean (true or false)', actualValue));
    }
  }
};

// Validate object
const validateObject = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  let isObject = false;

  if (actualValue && typeof actualValue === 'object' && !Array.isArray(actualValue)) {
    isObject = true;
  } else if (typeof actualValue === 'string') {
    try {
      const parsedValue = JSON.parse(actualValue);
      if (parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)) {
        isObject = true;
      }
    } catch (error) {
      // Invalid JSON
    }
  }

  if (!isObject) {
    errors.push(createValidationError(sheetName, header, rowIndex + 2, 'object', actualValue));
  }
};

// Validate positive number (optional)
const validateChoicePositiveNumber = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  const numberValue = actualValue?.toString().trim();
  if (numberValue) {
    const isNumeric = NUMERIC_REGEX.test(numberValue);
    const isPositive = parseFloat(numberValue) >= 0;

    if (!isNumeric || !isPositive) {
      errors.push(
        createValidationError(
          sheetName,
          header,
          rowIndex + 2,
          'positive number (integer or decimal-point)',
          actualValue
        )
      );
    }
  }
};

// Validate optional integer
const validateOptionalInteger = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  const numberValue = actualValue?.toString().trim();
  if (numberValue) {
    const isNumeric = INTEGER_REGEX.test(numberValue);
    const isPositive = parseFloat(numberValue) >= 0;

    if (!isNumeric || !isPositive) {
      errors.push(createValidationError(sheetName, header, rowIndex + 2, 'positive integer', actualValue));
    }
  }
};

const validateStockStatus = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  if (actualValue === null || actualValue === undefined) {
    errors.push(
      createValidationError(sheetName, header, rowIndex + 2, 'enum (inStock, outStock, limitStock)', actualValue, [
        'inStock',
        'outStock',
        'limitStock',
      ])
    );
    return;
  }

  validateEnumValues(sheetName, header, rowIndex, actualValue, ['inStock', 'outStock', 'limitStock'], errors);
};

const formatFieldName = (field: string): string => {
  return field
    .replace(/([a-z])([A-Z])/g, '$1 $2') // insert space before capital letters
    .replace(/^./, (str) => str.toUpperCase()); // capitalize first letter
};

const formatValidIds = <T>(ids: T[], message: string): string => {
  // Convert IDs to strings for consistent handling
  const stringIds = ids.map((id) => String(id));

  // Check if all IDs are numeric
  const numericIds = stringIds.map((id) => parseInt(id, 10)).filter((num) => !isNaN(num));

  if (numericIds.length === stringIds.length && numericIds.length > 0) {
    // All IDs are numeric, check if they form a continuous range
    const sortedIds = [...numericIds].sort((a, b) => a - b);
    const isSequential = sortedIds.every((id, index) => index === 0 || id === sortedIds[index - 1] + 1);

    if (isSequential && sortedIds.length > 2) {
      // Show as range if sequential and more than 2 items
      return `${message}: [${sortedIds[0]} - ${sortedIds[sortedIds.length - 1]}]`;
    }
  }

  // Fall back to preview format for non-sequential or non-numeric IDs
  const previewCount = 6;
  const previewValues = stringIds.slice(0, previewCount).join(', ');
  const moreCount = stringIds.length > previewCount ? `, and ${stringIds.length - previewCount} more` : '';
  return `${message}: ${previewValues}${moreCount}`;
};
/**
 * Validate a comma-separated list of IDs against a given set of IDs
 *
 * @param sheetName - The name of the sheet containing the cell to validate
 * @param header - The column header of the cell to validate
 * @param rowIndex - The row number of the cell to validate
 * @param actualValue - The value of the cell to validate
 * @param set - The set of valid IDs to validate against
 * @param errors - The array to push any validation errors to
 */
const validateMultipleIdsFromReferenceSheet = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  set: Set<string | number>,
  errors: ValidationError[]
): void => {
  const value = actualValue?.toString().trim() || '';
  if (!value) return;

  const idArray = value
    .split(',')
    .map((id: string) => id.trim())
    .filter(Boolean);

  const invalidIds = idArray.filter((id: string) => !set.has(id));

  if (invalidIds.length > 0) {
    const valuesArray = Array.from(set).map(String);

    // Function to format the valid IDs message

    const validIdsMessage = formatValidIds(valuesArray, 'Valid IDs');

    errors.push(
      createValidationError(
        sheetName,
        header,
        rowIndex + 2,
        `Please use only valid IDs from '${formatFieldName(header)}'.${validIdsMessage}.`,
        actualValue
      )
    );
  } // Allowed values
};

/**
 * Validates that the value is a valid ID from another sheet.
 *
 * @param sheetName - The name of the sheet containing the IDs to validate.
 * @param header - The column header of the value to validate.
 * @param rowIndex - The row number of the value to validate.
 * @param actualValue - The value to validate.
 * @param set - The set of valid IDs from the other sheet.
 * @param errors - The array to push any validation errors to.
 */
const validateParentIdFromSheet = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  set: Set<string | number>,
  errors: ValidationError[]
): void => {
  const value = actualValue?.toString().trim() || '';
  if (!value) return;

  const idArray = value
    .split(',')
    .map((id: string) => id.trim())
    .filter(Boolean);

  const invalidIds = idArray.filter((id: string) => !set.has(id));

  if (invalidIds.length > 0) {
    const valuesArray = Array.from(set).map(String);

    // Function to format the valid IDs message
    const validIdsMessage = formatValidIds(valuesArray, 'Valid IDs');

    if (invalidIds.length > 0) {
      errors.push(
        createValidationError(
          sheetName,
          header,
          rowIndex + 2,
          `Invalid parent ID. Check against the '${sheetName.toLowerCase()}' ID column. ${validIdsMessage}`,
          actualValue
        )
      );
    }
  }
};

// Validate multiple IDs
const validateMultipleIds = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  const idValues = actualValue?.toString().trim();
  const idArray = idValues?.split(',') || [];

  for (const id of idArray) {
    const trimmedId = id.trim();
    const isValidId = /^[1-9]\d*$/.test(trimmedId);

    if (!isValidId) {
      errors.push({
        sheetName,
        columnName: header,
        rowNumber: rowIndex + 2,
        expectedType: 'multiple positive integers (each ≥ 1, no decimals or zero)',
        actualValue: actualValue,
      });
      return;
    }
  }
  const key = `${sheetName}-${header}`;

  const allowedMappings: Record<string, keyof typeof idSets> = {
    'Category-menuIds': 'Menu',
    'Category-tagIds': 'Tag',
    'Item-addonIds': 'Item',
    'Item-tagIds': 'Tag',
    'Item-allergenIds': 'Allergen',
    'Modifier Group-modifierIds': 'Modifier',
    'Menu-settingId': 'Setting',
    'Item-settingId': 'Setting',
    'Category-settingId': 'Setting',
  };

  const idSetKey = allowedMappings[key];
  if (idSetKey) {
    validateMultipleIdsFromReferenceSheet(sheetName, header, rowIndex, actualValue, idSets[idSetKey], errors);
  }
};

// Validate color code

/**
 * Validate color code
 * @param sheetName sheet name
 * @param header column header
 * @param rowIndex row number
 * @param actualValue actual value
 * @param errors error list
 * @description
 * Validate color code.
 * 1. Check if the color code is in the format of #RRGGBB.
 * 2. Check if the color code is in the predefined list.
 * If any check fails, add the error to the error list.
 */
const validateColorCode = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  const colorCode = actualValue?.toString().trim() || '';
  const colorCodeRegex = /^#[0-9A-Fa-f]{6}$/;

  if (!colorCodeRegex.test(colorCode)) {
    errors.push(createValidationError(sheetName, header, rowIndex + 2, 'colorCode (#RRGGBB)', actualValue));
    return;
  }

  // Determine if this column should use MenuColors or CategoryColors
  const validColors =
    header.toLowerCase().includes('menu') || sheetName.toLowerCase().includes('menu') ? MenuColors : CategoryColors;

  if (!validColors.includes(colorCode.toUpperCase())) {
    errors.push(
      createValidationError(
        sheetName,
        header,
        rowIndex + 2,
        `Choose color from predefined list : ${validColors.join(', ')}`,
        actualValue
      )
    );
  }
};

// Validate email
const validateEmail = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  errors: ValidationError[]
): void => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (typeof actualValue !== 'string' || !emailRegex.test(actualValue)) {
    errors.push(createValidationError(sheetName, header, rowIndex + 2, 'email', actualValue));
  }
};

const getReferenceSheetName = (col: string): string => {
  const base = col.replace(/Id$/, '');

  // Optional: map specific cases
  const customMap: Record<string, string> = {
    modifierGroup: 'Modifier Group',
    modifierOption: 'Modifier Option',
  };

  return customMap[base] || capitalizeFirstLetter(base);
};

/**
 * Validate that a given sheet's data conforms to the expected type and has valid references.
 *
 * @param {RelationValidationConfig} config
 * @prop {string} sheetName - Name of the sheet being validated
 * @prop {string[][]} sheetData - 2D array of sheet data
 * @prop {ValidationError[]} errors - Array to store any validation errors
 * @prop {string[]} relationColumns - Columns that are part of the relation to be validated
 * @prop {Record<string, Set<string>>} referenceSets - Mapping of column names to their respective valid values
 * @prop {string} [relationName] - Optional name of the relation being validated
 */
export const validateSheetRelations = ({
  sheetName,
  sheetData,
  errors,
  relationColumns,
  referenceSets,
  relationName,
}: RelationValidationConfig): void => {
  const headers = sheetData[0];
  const rows = sheetData.slice(1);
  const indexes = relationColumns.map((col) => headers.indexOf(col));

  // If any of the required columns is missing, exit
  if (indexes.includes(-1)) return;

  const seenCombination = new Set<string>();

  rows.forEach((row, rowIndex) => {
    const values: string[] = indexes.map((idx) => String(row[idx] || '').trim());

    // Skip if any value is empty
    if (values.some((val) => !val)) return;

    // Validate each field's type and existence in reference set
    relationColumns.forEach((col, i) => {
      const rawValue = row[indexes[i]];
      const stringValue = values[i];

      validateNumber(sheetName, col, rowIndex, rawValue, errors);

      const referenceSet = referenceSets[col];
      if (referenceSet && !referenceSet.has(stringValue)) {
        const referenceSheetName = getReferenceSheetName(col);
        errors.push(
          createValidationError(
            sheetName,
            col,
            rowIndex + 2,
            `${formatFieldName(
              col
            )} contain invalid value: Expected reference to a valid ID from the "${referenceSheetName}" sheet.`,
            stringValue
          )
        );
      }
    });

    // Validate combination uniqueness
    const compositeKey = values.join('|');
    if (seenCombination.has(compositeKey)) {
      errors.push(
        createValidationError(
          sheetName,
          relationColumns.join('/'),
          rowIndex + 2,
          `Duplicate ${relationName || relationColumns.join(' + ')} combination not allowed`,
          compositeKey
        )
      );
    } else {
      seenCombination.add(compositeKey);
    }
  });
};

/**
 * Validates the columns of a modifier sheet.
 *
 * This function validates the columns of a modifier sheet against the following rules:
 *
 * 1. When `isNested` is FALSE and `addNested` is FALSE, `parentModifierId` must be empty.
 * 2. When `isNested` is TRUE, `addNested` must be FALSE.
 * 3. When `isNested` is TRUE, `parentModifierId` must be empty.
 * 4. When `addNested` is TRUE, `isNested` must be FALSE.
 * 5. When `addNested` is TRUE, `parentModifierId` must be a valid ID of a modifier with `isNested` set to TRUE.
 *
 * @param sheetName - The name of the sheet being validated.
 * @param sheetData - The data of the sheet being validated.
 * @param errors - The list of errors to append to.
 */
const validateModifierSheetColumns = (
  sheetName: string,
  sheetData: any[][],
  errors: ValidationError[],
  modifierOptions: Set<string>,
  modifierOptionCounts: { totalCounts: Map<string, number>; defaultCounts: Map<string, number> }
) => {
  const headers = sheetData[0];
  let rows = sheetData.slice(1);
  rows = rows.filter((row) => row.length > 0);
  const getIndex = (key: string) => headers.indexOf(key);
  const modifierMap = new Map<string, any[]>();

  // Build modifierMap
  rows.forEach((row) => {
    const id = String(row[getIndex('id')]).trim();
    if (id) modifierMap.set(id, row);
  });

  const toBoolean = (value: any): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.trim().toLowerCase() === 'true';
    return false;
  };

  // ========== BUILD: Parent to Children mapping ==========
  const parentToChildrenMap = new Map<string, string[]>();

  rows.forEach((row) => {
    const id = String(row[getIndex('id')]).trim();
    const parentModifierId = String(row[getIndex('parentModifierId')] || '').trim();
    const isNested = toBoolean(row[getIndex('isNested')]);

    if (id && isNested && parentModifierId) {
      if (!parentToChildrenMap.has(parentModifierId)) {
        parentToChildrenMap.set(parentModifierId, []);
      }
      parentToChildrenMap.get(parentModifierId)!.push(id);
    }
  });

  const handleNestedModifierScenario = (row: any[], rowIndex: number, modifierMap: Map<string, any[]>) => {
    const isNested = toBoolean(row[getIndex('isNested')]);
    const addNested = toBoolean(row[getIndex('addNested')]);
    const parentModifierId = String(row[getIndex('parentModifierId')] || '').trim();
    const canGuestSelectIdx = getIndex('canGuestSelectMoreModifiers');
    const price = Number(row[getIndex('price')]);
    const modifierOptionPriceType = row[getIndex('modifierOptionPriceType')];
    const multiSelectIdx = getIndex('multiSelect');
    const limitIndividualModifierSelectionIdx = getIndex('limitIndividualModifierSelection');
    const multiSelect = toBoolean(row[multiSelectIdx]);
    const limitIndividualModifierSelection = toBoolean(row[limitIndividualModifierSelectionIdx]);
    const isOptionalIdx = getIndex('isOptional');
    const minSelectorIdx = getIndex('minSelector');
    const isOptional = toBoolean(row[isOptionalIdx]);
    const minSelector = Number(row[minSelectorIdx]);
    const maxSelectorIdx = getIndex('maxSelector');
    const maxSelector = Number(row[maxSelectorIdx]);
    const rowNum = rowIndex + 2;

    const modTypeIdx = getIndex('modType');
    const modType = modTypeIdx !== -1 ? String(row[modTypeIdx] || '').trim() : '';

    const pushError = (column: string, expected: string, actual: any) => {
      errors.push(createValidationError(sheetName, column, rowNum, expected, actual));
    };

    const isSimpleModifier = !isNested && !addNested;
    const isNestedChildModifier = isNested && !addNested;
    const isParentModifier = !isNested && addNested;

    const rules = [
      {
        condition: !isNested && !addNested && parentModifierId,
        column: 'parentModifierId',
        expected: 'Must be empty when both "isNested" and "addNested" are FALSE',
        actual: parentModifierId,
      },
      {
        condition: isNested && addNested,
        column: 'isNested',
        expected: 'when isNested is true and addNested is true, this combination is not allowed.',
        actual: `isNested=${isNested}, addNested=${addNested}`,
      },
      {
        condition: addNested && parentModifierId,
        column: 'parentModifierId',
        expected: 'Must be empty when "addNested" is TRUE',
        actual: parentModifierId,
      },
    ];

    for (const rule of rules) {
      if (rule.condition) {
        pushError(rule.column, rule.expected, rule.actual);
      }
    }

    // ========== VALIDATION: modType based on isOptional ==========
    if (modTypeIdx !== -1) {
      const validOptionalTypes = ['optional', 'push optional'];
      const validRequiredTypes = ['required'];
      const modTypeLower = modType.toLowerCase();

      if (isOptional) {
        if (!validOptionalTypes.includes(modTypeLower)) {
          pushError(
            'modType',
            'When "isOptional" is TRUE, "modType" must be "Optional" or "Push Optional"',
            `isOptional=${isOptional}, modType="${modType}"`
          );
        }
      } else {
        if (!validRequiredTypes.includes(modTypeLower)) {
          pushError(
            'modType',
            'When "isOptional" is FALSE, "modType" must be "Required"',
            `isOptional=${isOptional}, modType="${modType}"`
          );
        }
      }
    }

    // ========== NEW VALIDATION: Parent Modifier must NOT have modifier options ==========
    if (isParentModifier) {
      const modifierId = String(row[getIndex('id')]).trim();

      if (modifierOptions.has(modifierId)) {
        pushError(
          'modifierId',
          `Parent Modifier (isNested=false, addNested=true) must NOT have modifier options directly linked. Modifier "${modifierId}" has options in modifierOptions.`,
          `modifierId="${modifierId}", optionsExist=true`
        );
      }
    }

    if (!addNested) {
      if (multiSelectIdx !== -1 && limitIndividualModifierSelectionIdx !== -1) {
        if (!multiSelect && limitIndividualModifierSelection) {
          pushError(
            'limitIndividualModifierSelection',
            '"limitIndividualModifierSelection" must be FALSE when "addNested" is FALSE and "multiSelect" is FALSE',
            row[limitIndividualModifierSelectionIdx]
          );
        }
      }

      if (isSimpleModifier || isNestedChildModifier) {
        if (isOptionalIdx !== -1 && minSelectorIdx !== -1) {
          if (isOptional && minSelector !== 0) {
            pushError(
              'minSelector',
              '"minSelector" must be 0 when "addNested" is FALSE and "isOptional" is TRUE',
              row[minSelectorIdx]
            );
          }

          if (!isOptional) {
            if (!Number.isFinite(minSelector) || minSelector < 1) {
              pushError(
                'minSelector',
                '"minSelector" must be greater or equal to 1 when "addNested" is FALSE and "isOptional" is FALSE',
                row[minSelectorIdx]
              );
            }
          }
        }
      }
    }

    if (addNested) {
      if (!Number.isFinite(price) || price !== 0) {
        pushError('price', 'Price must be 0 when "addNested" is TRUE', price);
      }

      if (modifierOptionPriceType?.toLowerCase() !== 'nocharge') {
        pushError(
          'modifierOptionPriceType',
          'modifierOptionPriceType must be "NoCharge" when "addNested" is TRUE',
          modifierOptionPriceType
        );
      }
    }

    if (parentModifierId) {
      const parentRow = modifierMap.get(parentModifierId);
      const parentIsNested = toBoolean(parentRow?.[getIndex('isNested')]);
      const parentAddNested = toBoolean(parentRow?.[getIndex('addNested')]);

      if (!parentRow || !(parentIsNested === false && parentAddNested === true)) {
        pushError(
          'parentModifierId',
          `parentModifierId must reference a Parent Modifier (isNested=false, addNested=true). ID "${parentModifierId}" is invalid`,
          parentModifierId
        );
      }
    }

    // Only require modifier options for Simple and Nested Child modifiers
    if (isSimpleModifier || isNestedChildModifier) {
      const id = String(row[getIndex('id')]).trim();
      if (!modifierOptions.has(id)) {
        pushError('modifierId', 'Modifier must have at least one valid modifier option', id);
      }
    }

    if (
      (isSimpleModifier || isNestedChildModifier) &&
      minSelectorIdx !== -1 &&
      Number.isFinite(minSelector) &&
      minSelector > 0
    ) {
      const modifierId = String(row[getIndex('id')]).trim();
      const optionCount = modifierOptionCounts.totalCounts.get(modifierId) || 0;

      if (minSelector > optionCount) {
        pushError(
          'minSelector',
          `minSelector value (${minSelector}) requires at least ${minSelector} modifier options, but only ${optionCount} option(s) ${
            optionCount === 1 ? 'is' : 'are'
          } linked to this modifier`,
          minSelector
        );
      }
    }

    if (canGuestSelectIdx !== -1) {
      const canGuestSelect = row[canGuestSelectIdx]?.toString().trim().toLowerCase();
      if (canGuestSelect === 'true' && maxSelector) {
        const modifierId = String(row[getIndex('id')]).trim();
        const defaultCount = modifierOptionCounts.defaultCounts.get(modifierId) || 0;
        if (Number.isFinite(maxSelector) && defaultCount > maxSelector) {
          pushError(
            'maxSelector',
            `Default modifier options count (${defaultCount}) cannot exceed max (${maxSelector})`,
            ``
          );
        }
      }

      if (addNested && canGuestSelect === 'true') {
        pushError(
          'canGuestSelectMoreModifiers',
          '"canGuestSelectMoreModifiers" cannot be TRUE when "addNested" is TRUE.',
          row[canGuestSelectIdx]
        );
      }
    }

    // ========== VALIDATION: Parent Modifier must have children ==========
    if (isParentModifier) {
      const modifierId = String(row[getIndex('id')]).trim();
      const children = parentToChildrenMap.get(modifierId) || [];

      if (children.length === 0) {
        pushError(
          'addNested',
          `Parent Modifier must have at least one Nested Child modifier referencing it via parentModifierId. No children found for "${modifierId}"`,
          `childrenCount=0`
        );
      }
    }
  };

  rows.forEach((row, i) => {
    if (row[getIndex('id')]) {
      handleNestedModifierScenario(row, i, modifierMap);
    }
  });
};

export const extractModifierOptions = (workbook: XLSX.WorkBook): Set<string> => {
  const modifierOptions = new Set<string>();
  const sheet = workbook.Sheets['Modifier ModifierOptions']; // Assuming sheet is named "Modifier Option"
  const data = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
  const headers = data[0];
  const rows = data.slice(1);
  const idIndex = headers?.indexOf('modifierId');
  if (idIndex !== -1) {
    rows.forEach((row) => {
      const modifierId = row[idIndex];
      if (modifierId) {
        modifierOptions.add(String(modifierId).trim());
      }
    });
  }
  return modifierOptions;
};

/**
 * Extracts the count of modifier options per modifier ID from the "Modifier ModifierOptions" sheet.
 * Returns a Map where key is modifierId (as string) and value is the count of options.
 */
export const extractModifierOptionCounts = (
  workbook: XLSX.WorkBook
): {
  totalCounts: Map<string, number>;
  defaultCounts: Map<string, number>;
} => {
  const totalCounts = new Map<string, number>();
  const defaultCounts = new Map<string, number>();

  const sheetNames = workbook.SheetNames || [];
  const actualSheetName = sheetNames.find((name) => name.trim().toLowerCase() === 'modifier modifieroptions');
  if (!actualSheetName) return { totalCounts, defaultCounts };

  const sheet = workbook.Sheets[actualSheetName];
  if (!sheet) return { totalCounts, defaultCounts };

  const data = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
  const headers = data[0];
  const rows = data.slice(1);
  const modifierIdIndex = headers?.indexOf('modifierId');
  const isDefaultSelectedIndex = headers?.indexOf('isDefaultSelected');

  if (modifierIdIndex !== -1) {
    rows.forEach((row) => {
      const modifierId = row[modifierIdIndex];
      if (modifierId !== undefined && modifierId !== null && modifierId !== '') {
        const modifierIdStr = String(modifierId).trim();

        // Count total options
        const currentTotal = totalCounts.get(modifierIdStr) || 0;
        totalCounts.set(modifierIdStr, currentTotal + 1);

        // Count default selected options (if column exists)
        if (isDefaultSelectedIndex !== -1) {
          const isDefaultSelected = row[isDefaultSelectedIndex];
          const isDefault =
            typeof isDefaultSelected === 'boolean'
              ? isDefaultSelected
              : String(isDefaultSelected).trim().toLowerCase() === 'true';

          if (isDefault) {
            const currentDefault = defaultCounts.get(modifierIdStr) || 0;
            defaultCounts.set(modifierIdStr, currentDefault + 1);
          }
        }
      }
    });
  }

  return { totalCounts, defaultCounts };
};
const parseCommaSeparatedIds = (value: unknown): string[] =>
  String(value ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

const buildSettingTypeMap = (workbook: XLSX.WorkBook): Map<string, SettingOwnerSheet> => {
  const settingSheet = workbook.Sheets['Setting'];
  const settingTypeById = new Map<string, SettingOwnerSheet>();
  if (!settingSheet) return settingTypeById;

  const settingData = XLSX.utils.sheet_to_json(settingSheet, { header: 1 }) as any[][];
  const headers = settingData[0] || [];
  const rows = settingData.slice(1);

  const idIndex = headers.indexOf('id');
  const typeIndex = headers.indexOf('type');
  if (idIndex === -1 || typeIndex === -1) return settingTypeById;

  const allowedTypes = new Set<SettingOwnerSheet>(['Menu', 'Category', 'Item']);

  rows.forEach((row) => {
    const id = String(row[idIndex] ?? '').trim();
    const type = String(row[typeIndex] ?? '').trim() as SettingOwnerSheet;
    if (!id || !allowedTypes.has(type)) return;
    settingTypeById.set(id, type);
  });

  return settingTypeById;
};

export const validateSettingIdTypeAndUniqueness = (workbook: XLSX.WorkBook, errors: ValidationError[]): void => {
  const settingTypeById = buildSettingTypeMap(workbook);
  const ownerSheets: SettingOwnerSheet[] = ['Menu', 'Category', 'Item'];

  /**
   * Track:
   * settingId -> sheetName -> usages[]
   */
  const usageMap = new Map<string, Map<SettingOwnerSheet, SettingUsage[]>>();

  ownerSheets.forEach((sheetName) => {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) return;

    const data = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
    const headers = data[0] || [];
    const rows = data.slice(1);

    const settingIdIndex = headers.indexOf('settingId');
    if (settingIdIndex === -1) return;

    rows.forEach((row, rowIndex) => {
      const rowNumber = rowIndex + 2;
      const rawSettingValue = row[settingIdIndex];
      const normalizedSettingValue = String(rawSettingValue ?? '').trim();
      if (!normalizedSettingValue) return;

      if (normalizedSettingValue.includes(',')) {
        errors.push(
          createValidationError(
            sheetName,
            'settingId',
            rowNumber,
            'settingId must contain exactly one ID (comma-separated values are not allowed)',
            rawSettingValue
          )
        );
        return;
      }

      const settingIds = parseCommaSeparatedIds(rawSettingValue);

      settingIds.forEach((settingId) => {
        const actualType = settingTypeById.get(settingId);

        // Type validation
        if (actualType && actualType !== sheetName) {
          errors.push(
            createValidationError(
              sheetName,
              'settingId',
              rowNumber,
              `settingId "${settingId}" belongs to type "${actualType}" and cannot be used in "${sheetName}"`,
              settingId
            )
          );
        }

        // Skip non-existent IDs (already handled by existing reference checks)
        if (!actualType) return;

        // Track usage
        if (!usageMap.has(settingId)) {
          usageMap.set(settingId, new Map());
        }

        const sheetMap = usageMap.get(settingId)!;

        if (!sheetMap.has(sheetName)) {
          sheetMap.set(sheetName, []);
        }

        sheetMap.get(sheetName)!.push({
          sheetName,
          rowNumber,
          settingId,
        });
      });
    });
  });

  // Uniqueness validation
  usageMap.forEach((sheetMap, settingId) => {
    const distinctSheets = Array.from(sheetMap.keys());

    // Same-sheet duplicates
    sheetMap.forEach((usages, sheetName) => {
      if (usages.length > 1) {
        usages.forEach((usage) => {
          errors.push(
            createValidationError(
              usage.sheetName,
              'settingId',
              usage.rowNumber,
              `settingId "${settingId}" is duplicated within "${sheetName}" sheet`,
              settingId
            )
          );
        });
      }
    });

    // Cross-sheet usage
    if (distinctSheets.length > 1) {
      const sheetNames = distinctSheets.join(', ');

      sheetMap.forEach((usages) => {
        usages.forEach((usage) => {
          errors.push(
            createValidationError(
              usage.sheetName,
              'settingId',
              usage.rowNumber,
              `settingId "${settingId}" is used across multiple sheets (${sheetNames})`,
              settingId
            )
          );
        });
      });
    }
  });
};

// Main validation function for cell values

const primitiveValidators: Record<
  string,
  (sheet: string, header: string, row: number, value: any, errors: ValidationError[]) => void
> = {
  string: validateString,
  '72Characters': (s, h, r, v, e) => validateCharacterLength(s, h, r, v, 72, e, 2),
  '40Characters': (s, h, r, v, e) => validateCharacterLength(s, h, r, v, 40, e, 2),
  '30Characters': (s, h, r, v, e) => validateCharacterLength(s, h, r, v, 30, e, 1),
  '60Characters': (s, h, r, v, e) => validateCharacterLength(s, h, r, v, 60, e, 2),
  phoneNumber: validatePhoneNumber,
  timezone: validateTimezone,
  imageUrl: validateImageUrl,
  time: validateTime,
  name: validateName,
  id: validateId,
  choiceNumber: validateChoiceNumber,
  number: validateNumber,
  positiveNumber: validateNonNegativeNumber,
  boolean: (s, h, r, v, e) => validateBoolean(s, h, r, v, e, false),
  choiceBoolean: (s, h, r, v, e) => validateBoolean(s, h, r, v, e, true),
  object: validateObject,
  choiceString: (s, h, r, v, e) => {
    if (v !== null && v !== undefined && typeof v !== 'string') {
      e.push(createValidationError(s, h, r + 2, 'string', v));
    }
  },
  choicePositiveNumber: validateChoicePositiveNumber,
  optionalInteger: validateOptionalInteger,
  multipleIds: validateMultipleIds,
  colorCode: validateColorCode,
  email: validateEmail,
  multiName: validateNameField,
  stockStatus: validateStockStatus,
  nonNegativeNumber: validateNonNegativeNumber,
  nonNegativeChoiceNumber: validateNonNegativeChoiceNumber,
};

const validateCellValue = (
  sheetName: string,
  header: string,
  rowIndex: number,
  actualValue: any,
  expectedType: string | string[] | number[],
  errors: ValidationError[]
): void => {
  if (Array.isArray(expectedType)) {
    validateEnumValues(sheetName, header, rowIndex, actualValue, expectedType, errors);
  } else if (typeof expectedType === 'object') {
    // Handle object validation (validateAttributes function would need to be implemented)
    validateAttributes(expectedType, actualValue, sheetName, header, rowIndex, createValidationError);
  } else {
    const validator = primitiveValidators[expectedType];
    if (validator) {
      validator(sheetName, header, rowIndex, actualValue, errors);
    }
  }
};

// Validate sheet data
export const validateSheetData = (
  sheetName: string,
  sheetData: any[][],
  expectedColumns: Record<string, string | string[] | number[]>,
  errors: ValidationError[]
): void => {
  const headers = sheetData[0];
  const rows = sheetData.slice(1);

  // Validate missing columns
  validateMissingColumns(sheetName, headers, Object.keys(expectedColumns), errors);

  // Validate duplicate IDs
  validateDuplicateIds(sheetName, headers, rows, errors);

  // TODO: We are just commenting it for testing purpose, we will enable it once we have the final confirmation on the sort order of sheets.
  validateSortOrderForSheets(sheetName, headers, rows, errors, [
    'Menu',
    'Category',
    'Item',
    'Modifier',
    'Modifier Option',
    'Modifier Group',
    'Category Items',
    'Category Modifiers',
    'Category ModifierGroups',
    'Item Modifiers',
    'Item Modifier Group',
    'Modifier ModifierOptions',
  ]);

  // Validate each row
  rows.forEach((row, rowIndex) => {
    // Skip completely empty rows
    if (row.every((cell) => cell === undefined)) return;

    // Normalize whitespace-only itemDescription values specifically for the Item sheet
    if (sheetName === 'Item') {
      const itemDescIdx = headers.indexOf('itemDescription');
      if (itemDescIdx !== -1) {
        const value = row[itemDescIdx];
        if (typeof value === 'string') {
          const trimmedValue = value.trim();
          if (trimmedValue === '') {
            row[itemDescIdx] = undefined;
          } else if (trimmedValue !== value) {
            row[itemDescIdx] = trimmedValue;
          }
        }
      }
    }

    // Check for empty cells
    if (validateEmptyCells(sheetName, headers, row, rowIndex, errors)) return;

    // Validate each cell
    headers.forEach((header, colIndex) => {
      const expectedType = expectedColumns[header];
      const actualValue = row[colIndex];
      validateCellValue(sheetName, header, rowIndex, actualValue, expectedType, errors);
    });

    // if (sheetName === 'Modifier') {
    //   validateCanGuestSelectMoreModifiers(sheetName, headers, row, rowIndex, errors);
    // }
    if (sheetName === 'Item') {
      validateItemMinMaxLimits(sheetName, headers, row, rowIndex, errors);
      validateItemPriceMaxLimit(sheetName, headers, row, rowIndex, errors);
    }
    if (sheetName === 'Modifier Group') {
      validateModifierGroupHasModifiers(sheetName, headers, row, rowIndex, errors);
    }
  });
};
/**
 * Validates a given workbook against a set of expected types.
 *
 * @param workbook - The workbook to validate.
 * @param expectedTypes - The expected types for each sheet.
 *
 * @returns A list of validation errors.
 */
export const validateEmployeeLocationWorkbook = (
  workbook: XLSX.WorkBook,
  expectedTypes: ExpectedTypes
): ValidationError[] => {
  const errors: ValidationError[] = [];
  const modifierOptions = extractModifierOptions(workbook);
  const counts = extractModifierOptionCounts(workbook);

  extractAllIdSetsFromSheets(workbook, idSets);
  const nestedModifierIds = extractNestedModifierIds(workbook);
  validateSheetExistence(workbook, expectedTypes, errors);

  const relationConfigs: Record<
    string,
    {
      relationColumns: [string, string];
      referenceSets: Record<string, Set<string>>;
      relationName: string;
    }
  > = {
    'Category Items': {
      relationColumns: ['categoryId', 'itemId'],
      referenceSets: {
        categoryId: idSets.Category,
        itemId: idSets.Item,
      },
      relationName: 'Category + Item',
    },
    'Item Modifiers': {
      relationColumns: ['itemId', 'modifierId'],
      referenceSets: {
        itemId: idSets.Item,
        modifierId: idSets.Modifier,
      },
      relationName: 'Item + Modifier',
    },
    'Category Modifiers': {
      relationColumns: ['categoryId', 'modifierId'],
      referenceSets: {
        categoryId: idSets.Category,
        modifierId: idSets.Modifier,
      },
      relationName: 'Category + Modifier',
    },
    'Category ModifierGroups': {
      relationColumns: ['categoryId', 'modifierGroupId'],
      referenceSets: {
        categoryId: idSets.Category,
        modifierGroupId: idSets.ModifierGroup,
      },
      relationName: 'Category + ModifierGroup',
    },
    'Item Modifier Group': {
      relationColumns: ['itemId', 'modifierGroupId'],
      referenceSets: {
        itemId: idSets.Item,
        modifierGroupId: idSets.ModifierGroup,
      },
      relationName: 'Item + ModifierGroup',
    },
    'Modifier ModifierOptions': {
      relationColumns: ['modifierId', 'modifierOptionId'],
      referenceSets: {
        modifierId: idSets.Modifier,
        modifierOptionId: idSets.ModifierOption,
      },
      relationName: 'Modifier + ModifierOption',
    },
  };

  workbook.SheetNames.forEach((sheetName) => {
    if (expectedTypes[sheetName]) {
      const sheet = workbook.Sheets[sheetName];
      const sheetData = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];

      validateSheetData(sheetName, sheetData, expectedTypes[sheetName], errors);

      const config = relationConfigs[sheetName];
      if (config) {
        validateSheetRelations({
          sheetName,
          sheetData,
          errors,
          relationColumns: config.relationColumns,
          referenceSets: config.referenceSets,
          relationName: config.relationName,
        });
      }

      if (sheetName.trim().toLowerCase() === 'category modifiers') {
        validateNoNestedModifiersLinked(sheetName, sheetData, 'modifierId', nestedModifierIds, errors);
      }
      if (sheetName.trim().toLowerCase() === 'item modifiers') {
        validateNoNestedModifiersLinked(sheetName, sheetData, 'modifierId', nestedModifierIds, errors);
      }

      if (sheetName === 'Modifier') {
        validateModifierSheetColumns(sheetName, sheetData, errors, modifierOptions, counts);
      }
    }
  });

  validateSettingIdTypeAndUniqueness(workbook, errors);

  return errors;
};

// Main validation function
export const validateEmployeeLocationSheetData = async (
  file: File,
  expectedTypes: ExpectedTypes
): Promise<ValidationError[]> => {
  return new Promise<ValidationError[]>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const errors = validateEmployeeLocationWorkbook(workbook, expectedTypes);
        resolve(errors);
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = (err) => reject(err);
    reader.readAsArrayBuffer(file);
  });
};
/**
 * Validates a given Buffer against a set of expected types.
 *
 * @param buffer - The Buffer to validate.
 * @param expectedTypes - The expected types for each sheet.
 *
 * @returns A list of validation errors.
 *
 * @throws Error - If validation fails.
 */
export const validateEmployeeLocationSheetDataBackend = async (
  buffer: Buffer,
  expectedTypes: ExpectedTypes
): Promise<ValidationError[]> => {
  try {
    const workbook = XLSX.read(buffer, { type: 'buffer' });
    const errors = validateEmployeeLocationWorkbook(workbook, expectedTypes);
    return errors;
  } catch (err) {
    throw new Error(`Validation failed: ${err}`);
  }
};

/**
 * Given a ValidationError, returns a string describing the error in a user-friendly
 * format.
 *
 * The returned string is a combination of the error message and the expected and
 * actual values. If the expected value is an enum, it will print the list of
 * allowed values. If the expected value is a specific type (e.g. boolean, number,
 * etc.), it will provide a brief description of the type.
 *
 * If the error is a custom error message, the function will return the error
 * message as is.
 *
 * @param error - The ValidationError to generate a string for.
 * @returns A string describing the error in a user-friendly format.
 */
export const getDetailedErrorMessage = (error: ValidationError): string => {
  // Handle special cases
  if (error.rowNumber === 0 && error.columnName === 'N/A') {
    return `The sheet '${error.sheetName}' is completely missing from the file.`;
  }

  if (error.rowNumber === 0) {
    return `The required column '${error.columnName}' is missing from sheet '${error.sheetName}'.`;
  }

  // Special case: specific error for 'stationIds' enum
  if (error.expectedType.includes('enum') && error.columnName === 'stationIds') {
    return `The Station ID value ${error.actualValue} is not valid.`;
  }

  // Map expectedType keywords to messages
  const typeMessageMap: Record<string, string> = {
    enum: `Current value ${error.actualValue} is not valid.`,
    'Unique id': 'This ID value already exists in another row. Each ID must be unique within the sheet.',
    'Non-empty text': 'This field is required and cannot be empty, blank, or contain only spaces..',
    phoneNumber: 'Phone number must be exactly 11 digits (e.g., +12345678901).',
    email: 'Please enter a valid email address (e.g., user@example.com).',
    time: 'Time must be in hh:mm:ss format (e.g., 14:30:00).',
    timezone: 'Please enter a valid timezone (e.g., Eastern Time, Pacific Time, Mountain Time).',
    imageUrl: 'URL must point to a valid image file (.jpeg, .jpg, .png, .gif, .bmp, .webp).',
    colorCode: 'Color code must be in hexadecimal format (e.g., #FF0000 for red).',
    boolean: 'Value must be either "true" or "false".',
    'multiple ids': 'Enter comma-separated IDs, each must be a number greater than 1 (e.g., 2,5,10).',
    'only numeric values': 'ID must contain only whole numbers.',
  };

  // Handle exact matches or substring presence
  for (const key in typeMessageMap) {
    if (error.expectedType.includes(key)) {
      return typeMessageMap[key];
    }
  }
  if (
    error.expectedType.includes('Invalid character(s) found') ||
    error.expectedType.includes('Missing required value')
  ) {
    return error.expectedType;
  }

  // Handle "characters" range constraint dynamically
  if (error.expectedType.includes('characters')) {
    return `This field is required. The text must be between ${error.expectedType.toLowerCase()}.`;
  }
  if (error.expectedType.includes('Employee')) {
    return error.expectedType;
  }

  if (error.expectedType.includes('Nested modifier')) {
    return 'Nested modifier should not be directly linked to Category/Item. Only top-level modifiers can be linked; nested modifiers are options under another modifier.';
  }

  if (error.expectedType.includes('Item price must not exceed')) {
    return `Item price must not exceed ${ITEM_PRICE_MAX}. The value provided is too high; maximum allowed is ${ITEM_PRICE_MAX} (same as frontend limit).`;
  }

  if (error.expectedType.includes('Modifier Group must have at least one modifier')) {
    return 'Modifier Group must have at least one modifier. Please provide at least one valid modifier ID in the modifierIds field.';
  }

  if (error.expectedType.includes('sortOrder must be unique')) {
    if (error.expectedType.includes('within')) {
      // Junction table error: includes parent context
      return error.expectedType;
    }
    // Direct sheet error
    return 'sortOrder must be unique within the sheet. Each row must have a different sortOrder value.';
  }

  if (error.expectedType.includes('sortOrder must not exceed')) {
    return error.expectedType;
  }

  if (error.expectedType.includes('when isNested is true and addNested is true')) {
    return 'when isNested is true and addNested is true, this combination is not allowed.';
  }

  if (error.expectedType.includes('minSelector value') && error.expectedType.includes('requires at least')) {
    return error.expectedType;
  }

  if (error.expectedType.includes('number')) {
    if (error.expectedType.includes('positive number')) {
      return 'Item price cannot be negative. Value must be 0 or greater.';
    }
    return error.expectedType.includes('positive')
      ? 'Value must be a positive number (0 or greater).'
      : 'Value must be a positive whole number.';
  }

  // Custom fallback message
  const isCustomMessage =
    !/^(enum|boolean|number|Non-empty|Unique|email|time|timezone|imageUrl|colorCode|characters|multiple ids|alphabets|only numeric)/i.test(
      error.expectedType
    );

  if (isCustomMessage) {
    return `Error: ${error.expectedType}`;
  }

  // Final fallback
  return `Sheet: ${error.sheetName} | Row: ${error.rowNumber} | Column: ${error.columnName}\n→ Expected: ${
    error.expectedType
  }. Found: ${
    error.actualValue === null || error.actualValue === undefined ? 'empty value' : `"${error.actualValue}"`
  }`;
};

/**
 * Format an array of ValidationError objects into a human-readable string array.
 *
 * The resulting strings will contain the following information:
 *  - The sheet name
 *  - The row and column number (if applicable)
 *  - A description of the error (e.g. "Found: Empty/Missing")
 *  - The allowed values (if applicable)
 *  - A detailed error message
 *
 * @param errorList - The array of ValidationError objects to format.
 * @returns An array of strings, each describing one error.
 */
// export function formatValidationErrors(errorList: ValidationError[]): string[] {
//   return errorList.map((error) => {
//     let location = '';

//     if (error.rowNumber === 0 && error.columnName === 'N/A') {
//       location = `Sheet: ${error.sheetName}`;
//     } else if (error.rowNumber === 0) {
//       location = `Sheet: ${error.sheetName} | Column: ${error.columnName}`;
//     } else {
//       location = `Sheet: ${error.sheetName} | Row: ${error.rowNumber} | Column: ${error.columnName}`;
//     }

//     let found = '';

//     if (error.actualValue === null || error.actualValue === undefined) {
//       found = 'Found: Empty/Missing';
//     } else if (typeof error.actualValue === 'string' && error.actualValue.trim() === '') {
//       found = 'Found: Empty String';
//     } else {
//       found = `Found: ${JSON.stringify(error.actualValue)}`;
//     }
//     const allowed =
//       error?.allowedValues && error?.allowedValues?.length > 0 ? `Allowed: ${error?.allowedValues.join(', ')}` : null;

//     const detailed = getDetailedErrorMessage(error);

//     return [location, found, allowed, detailed].filter(Boolean).join('/n');
//   });
// }

export function formatValidationErrors(errorList: ValidationError[]): FormattedValidationError[] {
  return errorList.map((error) => {
    const location =
      error.rowNumber === 0 && error.columnName === 'N/A'
        ? `Sheet: ${error.sheetName}`
        : error.rowNumber === 0
        ? `Sheet: ${error.sheetName} | Column: ${error.columnName}`
        : `Sheet: ${error.sheetName} | Row: ${error.rowNumber} | Column: ${error.columnName}`;

    const found =
      error.actualValue === null || error.actualValue === undefined
        ? 'Found: Empty/Missing'
        : typeof error.actualValue === 'string' && error.actualValue.trim() === ''
        ? 'Found: Empty String'
        : `Found: ${JSON.stringify(error.actualValue)}`;

    const allowed =
      error.allowedValues && error.allowedValues.length > 0 ? `Allowed: ${error.allowedValues.join(', ')}` : null;

    const detailed = getDetailedErrorMessage(error);

    return {
      sheetName: error.sheetName,
      rowNumber: error.rowNumber,
      columnName: error.columnName,
      location,
      found,
      allowed,
      detailed,
      actualValue: error.actualValue,
      allowedValues: error.allowedValues,
    };
  });
}
/**
 * Validates that for a given row in the 'Item' sheet, the 'maxLimit' value is greater than the 'minLimit' value.
 * This function assumes basic type validation (e.g., 'choiceNumber') has already been performed.
 *
 * @param sheetName - The name of the sheet (should be 'Item').
 * @param headers - The array of column headers.
 * @param row - The array of cell values for the current row.
 * @param rowIndex - The index of the current row (0-based for data rows).
 * @param errors - The array to push any validation errors to.
 */
const validateItemMinMaxLimits = (
  sheetName: string,
  headers: string[],
  row: any[],
  rowIndex: number,
  errors: ValidationError[]
): void => {
  // This validation is only for the 'Item' sheet
  if (sheetName !== 'Item') return;

  const minLimitIndex = headers.indexOf('minLimit');
  const maxLimitIndex = headers.indexOf('maxLimit');

  // If either column is missing, skip this validation
  if (minLimitIndex === -1 || maxLimitIndex === -1) return;

  const minLimitValue = row[minLimitIndex];
  const maxLimitValue = row[maxLimitIndex];

  // If either value is empty/undefined, we can skip the comparison (as they are optional 'choiceNumber' fields)
  if (minLimitValue === null || minLimitValue === undefined || minLimitValue.toString().trim() === '') return;
  if (maxLimitValue === null || maxLimitValue === undefined || maxLimitValue.toString().trim() === '') return;

  // Convert to numbers for comparison
  const minLimitNum = parseFloat(minLimitValue.toString().trim());
  const maxLimitNum = parseFloat(maxLimitValue.toString().trim());

  // Check if conversion was successful (should be, as 'choiceNumber' validator ran first)
  if (isNaN(minLimitNum) || isNaN(maxLimitNum)) return;

  // Validate that maxLimit is greater than minLimit
  if (maxLimitNum < minLimitNum) {
    errors.push(
      createValidationError(
        sheetName,
        'maxLimit',
        rowIndex + 2,
        'Max Limit must be greater than Min Limit',
        maxLimitValue,
        [`Min Limit for this row is: ${minLimitValue}`]
      )
    );
  }
};

/** Maximum allowed item price (must match frontend validation). */
const ITEM_PRICE_MAX = 9999;

/**
 * Validates that itemPrice in the Item sheet does not exceed the maximum allowed (9999).
 */
const validateItemPriceMaxLimit = (
  sheetName: string,
  headers: string[],
  row: any[],
  rowIndex: number,
  errors: ValidationError[]
): void => {
  if (sheetName !== 'Item') return;

  const itemPriceIndex = headers.indexOf('itemPrice');
  if (itemPriceIndex === -1) return;

  const itemPriceValue = row[itemPriceIndex];
  if (itemPriceValue === null || itemPriceValue === undefined) return;

  const str = String(itemPriceValue).trim();
  if (str === '') return;

  const num = parseFloat(str);
  if (isNaN(num)) return;

  if (num > ITEM_PRICE_MAX) {
    errors.push(
      createValidationError(
        sheetName,
        'itemPrice',
        rowIndex + 2,
        `Item price must not exceed ${ITEM_PRICE_MAX}`,
        itemPriceValue,
        [`Maximum allowed value is ${ITEM_PRICE_MAX}`]
      )
    );
  }
};

/**
 * Validates that Modifier Group has at least one modifier ID.
 * A Modifier Group must have at least one modifier to be valid.
 */
const validateModifierGroupHasModifiers = (
  sheetName: string,
  headers: string[],
  row: any[],
  rowIndex: number,
  errors: ValidationError[]
): void => {
  if (sheetName !== 'Modifier Group') return;

  const modifierIdsIndex = headers.indexOf('modifierIds');
  if (modifierIdsIndex === -1) return;

  const modifierIdsValue = row[modifierIdsIndex];

  // Check if value is empty, null, undefined, or just whitespace
  if (
    modifierIdsValue === null ||
    modifierIdsValue === undefined ||
    modifierIdsValue === '' ||
    (typeof modifierIdsValue === 'string' && modifierIdsValue.trim() === '')
  ) {
    errors.push(
      createValidationError(
        sheetName,
        'modifierIds',
        rowIndex + 2,
        'Modifier Group must have at least one modifier',
        modifierIdsValue || 'Empty/Missing'
      )
    );
    return;
  }

  // Check if after splitting by comma, there's at least one valid ID
  const idValues = String(modifierIdsValue).trim();
  const idArray = idValues
    .split(',')
    .map((id: string) => id.trim())
    .filter(Boolean);

  if (idArray.length === 0) {
    errors.push(
      createValidationError(
        sheetName,
        'modifierIds',
        rowIndex + 2,
        'Modifier Group must have at least one modifier',
        modifierIdsValue
      )
    );
  }
};

/**
 * Validates that 'canGuestSelectMoreModifiers' cannot be TRUE when 'addNested' is TRUE.
 * Otherwise, shows a validation error.
 */
export function validateCanGuestSelectMoreModifiers(
  sheetName: string,
  headers: string[],
  row: any[],
  rowIndex: number,
  errors: ValidationError[]
): void {
  const canGuestSelectIdx = headers.indexOf('canGuestSelectMoreModifiers');
  const addNestedIdx = headers.indexOf('addNested');

  if (canGuestSelectIdx === -1 || addNestedIdx === -1) return;

  const canGuestSelect = row[canGuestSelectIdx]?.toString().trim().toLowerCase();
  const addNested = row[addNestedIdx]?.toString().trim().toLowerCase() === 'true';

  // Only show error if addNested is TRUE and canGuestSelectMoreModifiers is TRUE
  if (addNested && canGuestSelect === 'true') {
    errors.push(
      createValidationError(
        sheetName,
        'canGuestSelectMoreModifiers',
        rowIndex + 2,
        '"can guest select" cannot be TRUE when "addNested" is TRUE.',
        row[canGuestSelectIdx]
      )
    );
  }
}
